/**
 * Page-global attention tracking: which editor does the user's attention currently
 * live in? Multiple editors share a page (same pattern as the drag clipboard), and a
 * contextual surface may only stay up in the attended editor — an editor gets no
 * focus/blur/transaction event of its own when the user moves to a sibling editor,
 * so this document-level boundary is the only one that can observe the move.
 *
 * One pair of document capture listeners, installed while any root is registered.
 * Editors register their container plus their floating portal (toolbar, link menu)
 * so interacting with those counts as attention on their editor. Null = attention is
 * outside every registered root (e.g. the surrounding app chrome) — that is NOT
 * "another editor", so it suppresses nothing.
 */

type Root = { editorId: string; onChange: () => void }

let attendedEditorId: string | null = null
const roots = new Map<HTMLElement, Root>()

export function getAttendedEditorId(): string | null {
  return attendedEditorId
}

const updateFromEvent = (event: Event) => {
  const target = event.target
  if (!(target instanceof Node)) return
  let next: string | null = null
  for (const [element, root] of roots) {
    if (element.contains(target)) {
      next = root.editorId
      break
    }
  }
  if (next === attendedEditorId) return
  attendedEditorId = next
  // An attention move concerns at most two editors, but every arbiter re-resolves —
  // the resolve is a handful of boolean checks, and unchanged verdicts propagate
  // nothing. Snapshot the callbacks: one may unregister roots while we iterate.
  for (const root of [...roots.values()]) root.onChange()
}

/** Register `element` as belonging to `editorId`. Returns the unregister. */
export function registerAttentionRoot(
  editorId: string,
  element: HTMLElement,
  onChange: () => void,
): () => void {
  if (roots.size === 0) {
    document.addEventListener("focusin", updateFromEvent, true)
    document.addEventListener("pointerdown", updateFromEvent, true)
  }
  roots.set(element, { editorId, onChange })
  return () => {
    roots.delete(element)
    if (roots.size === 0) {
      document.removeEventListener("focusin", updateFromEvent, true)
      document.removeEventListener("pointerdown", updateFromEvent, true)
      attendedEditorId = null
    }
  }
}
